import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUp, Check, Copy, History, Loader2, Maximize2, MessageSquarePlus, Trash2, TriangleAlert, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { useLocation } from "react-router-dom";
import { COPILOT_SUGGESTIONS, PAGE_SUGGESTION, conversationDate, openPage } from "../../lib/copilot";
import { formatPercent, shareTone, todayShare } from "../../lib/assistantUsage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import CopilotText from "./CopilotText";
import CopilotLogo from "./CopilotLogo";
import ProposalCard from "./ProposalCard";

// The content of the bubble's panel, the same on a computer and on a phone: conversation, history, question field. Its pieces
// (messages, question field, conversation list) are shared with the full page (pages/CopilotPage.jsx).
export default function CopilotChat({ state, actions, onClose, onNavigate, onExpand, inputRef, showClose }) {
  const { view, conversations, loadingHistory, title, streaming } = state;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white" data-testid="copilot-chat">
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
        <CopilotLogo size={32} />
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-semibold text-slate-900">Copilote Jamora</div>
          <div className="truncate text-xs text-slate-500">{view === "history" ? "Vos conversations" : title || "Nouvelle conversation"}</div>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={view === "history" ? "Revenir à la conversation" : "Voir mes conversations"}
          aria-pressed={view === "history"} onClick={actions.toggleHistory} data-testid="copilot-history-toggle">
          <History size={16} />
        </Button>
        {onExpand && (
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ouvrir le Copilote en pleine page" title="Agrandir"
            onClick={onExpand} data-testid="copilot-expand">
            <Maximize2 size={16} />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Nouvelle conversation" onClick={actions.newConversation}
          disabled={streaming} data-testid="copilot-new">
          <MessageSquarePlus size={16} />
        </Button>
        {showClose && (
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Fermer le Copilote" onClick={onClose} data-testid="copilot-close">
            <X size={16} />
          </Button>
        )}
      </div>

      {view === "history" ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2" data-testid="copilot-history">
          <ConversationList conversations={conversations} loading={loadingHistory} onOpen={actions.openConversation}
            onDelete={actions.deleteConversation} />
        </div>
      ) : (
        <CopilotMessages state={state} actions={actions} onNavigate={onNavigate} className="px-4 py-4" />
      )}

      <div className="border-t border-slate-100 px-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] pt-3 md:pb-3">
        <CopilotComposer state={state} actions={actions} inputRef={inputRef} />
      </div>
    </div>
  );
}

/** The exchanges of the conversation, with its approval cards; the suggestions when it is empty (unless `hideEmpty`). */
export function CopilotMessages({ state, actions, onNavigate, className = "", innerClassName = "", hideEmpty = false }) {
  const { messages, streaming, status, availability, deciding } = state;
  const scrollRef = useRef(null);
  const unavailable = availability && !availability.available;

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, status]);

  return (
    <div ref={scrollRef} className={`min-h-0 flex-1 overflow-y-auto ${className}`} data-testid="copilot-messages" aria-live="polite">
      <div className={`space-y-4 ${innerClassName}`}>
        {messages.length === 0 && !unavailable && !hideEmpty && (
          <div className="space-y-3 pt-4">
            <p className="text-sm text-slate-600">Posez une question sur vos prospects, tâches, missions ou finances. Le Copilote ne voit que ce que vous avez le droit de voir.</p>
            <Suggestions onPick={actions.ask} disabled={streaming} />
          </div>
        )}
        {messages.map((m, index) => (
          m.role === "user" ? (
            <div key={m.id} className="group flex flex-col items-end gap-1">
              <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-jamora px-3 py-2 text-sm text-white">{m.text}</div>
              {m.text && <CopyMessageButton text={m.text} label="Copier la question" />}
            </div>
          ) : (
            <div key={m.id} className="group space-y-1.5" data-testid="copilot-answer">
              {m.text && <CopilotText text={m.text} onNavigate={onNavigate} />}
              {/* An answer still being written is not offered: half a text would be copied. */}
              {m.text && !(streaming && index === messages.length - 1) && <CopyMessageButton text={m.text} label="Copier la réponse" />}
              {m.error && (
                <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <TriangleAlert size={14} className="mt-0.5 shrink-0" />{m.error}
                </div>
              )}
              {m.proposal && (
                <ProposalCard proposal={m.proposal} busy={deciding === m.proposal.id || streaming} onNavigate={onNavigate}
                  onAccept={(body) => actions.acceptProposal(m.proposal, body)} onRefuse={(reason) => actions.refuseProposal(m.proposal, reason)}
                  onEdit={m.proposal.edit?.form === "task" ? () => actions.editProposal(m.proposal) : null} />
              )}
            </div>
          )
        ))}
        {streaming && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="copilot-status">
            <CopilotLogo variant="thinking" size={22} />{status || "Le Copilote réfléchit…"}
          </div>
        )}
      </div>
    </div>
  );
}

/** « Copier » under a message: shown on hover or keyboard focus on a computer, always on a touch screen (no hover there). */
function CopyMessageButton({ text, label }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      toast.error("Impossible de copier : votre navigateur a refusé l'accès au presse-papiers.");
    }
  };

  return (
    <button type="button" onClick={copy} aria-label={copied ? "Copié" : label} title={copied ? "Copié" : label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition-opacity hover:bg-slate-100 hover:text-slate-700 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora/40 md:opacity-0 md:group-hover:opacity-100"
      data-testid="copilot-copy">
      {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
    </button>
  );
}

export function Suggestions({ onPick, disabled, className = "flex flex-col items-start gap-2" }) {
  const onPage = Boolean(openPage(useLocation()));
  const suggestions = onPage ? [PAGE_SUGGESTION, ...COPILOT_SUGGESTIONS.slice(0, 2)] : COPILOT_SUGGESTIONS;
  return (
    <div className={className}>
      {suggestions.map((s) => (
        <button key={s} type="button" onClick={() => !disabled && onPick(s)} disabled={disabled}
          className="rounded-full border border-slate-200 px-3 py-1.5 text-left text-xs text-slate-700 hover:border-jamora/40 hover:bg-jamora/5 disabled:opacity-50">
          {s}
        </button>
      ))}
    </div>
  );
}

const RING_COLORS = { ok: "text-emerald-500", warn: "text-amber-500", high: "text-red-500" };

/** How much of the day's cap is spent, as a small ring right after the line « Aujourd'hui : … » under the question field. */
function TodayRing({ share }) {
  const tone = shareTone(share);
  const radius = 7;
  const length = 2 * Math.PI * radius;
  const label = `Aujourd'hui : ${formatPercent(share)} de votre plafond journalier`;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span role="img" aria-label={label} tabIndex={0} data-testid="copilot-today-ring" data-tone={tone}
            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora/30">
            <svg viewBox="0 0 18 18" className="h-3.5 w-3.5 -rotate-90" aria-hidden="true">
              <circle cx="9" cy="9" r={radius} fill="none" strokeWidth="2.5" className="stroke-slate-200" />
              <circle cx="9" cy="9" r={radius} fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"
                strokeDasharray={length} strokeDashoffset={length * (1 - share)} className={`${RING_COLORS[tone]} transition-[stroke-dashoffset] duration-500`} />
            </svg>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** The question field and the AI notice (with the person's spending of the day). `large`: the page's roomy version. */
export function CopilotComposer({ state, actions, inputRef, large = false }) {
  const { streaming, availability } = state;
  const [draft, setDraft] = useState("");
  const share = todayShare(availability?.today);
  const unavailable = availability && !availability.available;
  const fieldRef = useRef(null);
  const setField = (node) => {
    fieldRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef) inputRef.current = node;
  };
  // The field grows with what is typed (a long question stays readable) up to its max height (CSS), then scrolls; empty again = back to its minimum.
  useLayoutEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    field.style.height = "auto";
    // scrollHeight leaves the border out, the field's height (border-box) does not: add it back or the text scrolls by two pixels.
    field.style.height = `${field.scrollHeight + (field.offsetHeight - field.clientHeight)}px`;
  }, [draft, large, unavailable]);

  const send = () => {
    const question = draft.trim();
    if (!question || streaming || unavailable) return;
    setDraft("");
    actions.ask(question);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div>
      {unavailable ? (
        <p role="status" className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="copilot-unavailable">{availability.message}</p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); send(); }}
          className={large ? "flex items-end gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm focus-within:border-jamora/40 focus-within:ring-2 focus-within:ring-jamora/15" : "flex items-end gap-2"}>
          <label htmlFor="copilot-input" className="sr-only">Votre question</label>
          <textarea id="copilot-input" ref={setField} rows={large ? 2 : 1} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown}
            maxLength={4000} placeholder={large ? "Comment puis-je vous aider ?" : "Posez votre question…"} data-testid="copilot-input"
            className={large
              ? "max-h-[min(18rem,35vh)] min-h-[56px] flex-1 resize-none overflow-y-auto bg-transparent px-1 py-1 text-base outline-none"
              : "max-h-[min(12rem,30vh)] min-h-[40px] flex-1 resize-none overflow-y-auto rounded-lg border border-slate-200 px-3 py-2 text-base outline-none focus-visible:ring-2 focus-visible:ring-jamora/30 md:text-sm"} />
          <Button type="submit" size="icon" className="h-10 w-10 shrink-0" disabled={!draft.trim() || streaming} aria-label="Envoyer la question" data-testid="copilot-send">
            {streaming ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} />}
          </Button>
        </form>
      )}
      <p className="mt-2 flex flex-wrap items-center justify-center gap-x-1 text-center text-2xs text-slate-400">
        <span>Réponses générées par l'IA : vérifiez sur la fiche avant d'agir.</span>
        {share != null && (
          <span className="inline-flex items-center gap-1.5" data-testid="copilot-today">
            · Aujourd'hui : {formatPercent(share)} de votre plafond <TodayRing share={share} />
          </span>
        )}
      </p>
    </div>
  );
}

/** The person's conversations, newest first, each deletable after a confirmation. `currentId` is highlighted (the page). */
export function ConversationList({ conversations, loading, onOpen, onDelete, currentId = null, compact = false }) {
  const [toDelete, setToDelete] = useState(null);
  return (
    <>
      {loading && !conversations && <div className="space-y-2 p-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>}
      {conversations?.length === 0 && (
        <p className="px-3 py-8 text-center text-sm text-slate-500">Aucune conversation pour le moment.</p>
      )}
      {conversations?.map((c) => (
        <div key={c.id} className={`group flex items-center gap-1 rounded-lg ${c.id === currentId ? "bg-jamora/10" : "hover:bg-slate-50"}`}>
          <button type="button" onClick={() => onOpen(c.id)} className={`min-w-0 flex-1 px-3 text-left ${compact ? "py-2" : "py-2.5"}`}
            aria-current={c.id === currentId ? "true" : undefined} data-testid={`copilot-conversation-${c.id}`}>
            <div className={`truncate text-sm ${c.id === currentId ? "font-medium text-jamora" : "text-slate-800"}`}>{c.title}</div>
            {!compact && <div className="text-xs text-slate-400">{conversationDate(c.updated_at)}</div>}
          </button>
          <Button variant="ghost" size="icon"
            className={`h-8 w-8 shrink-0 text-slate-400 hover:text-red-600 ${compact ? "opacity-0 focus-visible:opacity-100 group-hover:opacity-100" : ""}`}
            aria-label={`Supprimer « ${c.title} »`} onClick={() => setToDelete(c)}>
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette conversation ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.title} » sera effacée définitivement de votre historique.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { onDelete(toDelete.id); setToDelete(null); }}
              data-testid="copilot-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
